import { STANDARD_DOCUMENT_COLUMNS } from '@/helpers/documentDisplay'

export const DOCUMENT_FIELD_TYPES = ['invoice', 'credit_note', 'warranty', 'other']
export const DOCUMENT_DATA_TYPES = ['text', 'number', 'date']

// Technical keys accept ASCII letters, digits, underscores and hyphens.
const DOCUMENT_FIELD_KEY_PATTERN = /^[A-Za-z0-9_-]+$/
// Labels accept Unicode letters and numbers plus common display punctuation. Mirrors isValidLabelValue
// (src/schemas/validators/labels.js in oms-api-v2), which rejects quotes and apostrophes: a label the front
// accepts and the api refuses surfaces as a save failure with no indication of the offending field.
const DOCUMENT_FIELD_LABEL_PATTERN = /^[\p{L}\p{N}\s\-_./&#()[\]]+$/u
const LETTER_OR_NUMBER_PATTERN = /[\p{L}\p{N}]/u

/**
 * Create an empty editable custom field.
 * @returns {Object} A new active field with independent empty collections.
 */
export function newDocumentField() {
  return { document_types: [], key: '', labels: {}, data_type: '', active: true, description: '' }
}

/**
 * Copy a field into its API payload, trimming labels and omitting empty translations.
 * @param {Object} field Editable custom field.
 * @returns {Object} Custom field payload.
 */
export function toDocumentFieldPayload(field) {
  return {
    document_types: [...(field.document_types || [])],
    key: field.key,
    labels: Object.fromEntries(
      Object.entries(field.labels || {})
        .filter(([, value]) => value?.trim())
        .map(([locale, value]) => [locale, value.trim()])
    ),
    data_type: field.data_type,
    active: field.active,
    // Dropped when blank rather than sent as an empty string, like the labels above: JSON.stringify
    // omits an undefined value, and the api has no default on this path, so every field without a
    // description would otherwise store one.
    description: field.description?.trim() || undefined,
  }
}

// Keys are unique per tenant; an empty type list applies to all documents.
/**
 * Validate field values and uniqueness while allowing the original key during editing.
 * @param {Object} field Draft field to validate.
 * @param {Object[]} fields Existing tenant fields.
 * @param {string|null} [originalIdentity=null] Key of the field being edited.
 * @returns {Object<string, string>} Error translation keys indexed by input name.
 */
export function documentFieldErrors(field, fields, originalIdentity = null) {
  const errors = {}
  if (
    !Array.isArray(field.document_types) ||
    field.document_types.some((type) => !DOCUMENT_FIELD_TYPES.includes(type))
  ) {
    errors.document_types = 'required'
  }
  if (typeof field.key !== 'string' || !DOCUMENT_FIELD_KEY_PATTERN.test(field.key)) {
    errors.key = 'invalidKey'
  } else if (STANDARD_DOCUMENT_COLUMNS.includes(field.key)) {
    // The api refuses these twice, as custom_field_reserved_key and in the schema validator. Letting one
    // through would block every later save of the screen behind a message that names no field — and the
    // key would shadow a standard column in the available list.
    errors.key = 'reservedKey'
  }
  if (!DOCUMENT_DATA_TYPES.includes(field.data_type)) {
    errors.data_type = 'required'
  }
  if (!field.labels?.en?.trim()) {
    errors.en = 'englishRequired'
  }
  for (const [locale, label] of Object.entries(field.labels || {})) {
    if (label?.trim() && (!DOCUMENT_FIELD_LABEL_PATTERN.test(label.trim()) || !LETTER_OR_NUMBER_PATTERN.test(label))) {
      errors[locale] = 'invalidLabel'
    }
  }
  // The key alone identifies an entry. It used to be the key paired with a document type, until the
  // api dropped that: a key collided with itself across types, declaring two data types with nothing
  // to arbitrate. So uniqueness ignores document_types, and an inactive entry still holds its key.
  if (fields.some((item) => item.key !== originalIdentity && item.key === field.key)) {
    errors.key = 'duplicate'
  }
  return errors
}
